// As quatro assinaturas de movimento do canvas da marca.
// Tudo em CSS/SVG puro, sem imagens. Cada um lê --accent do pai.
import type { CSSProperties, FC } from 'react';
import type { MotionKind } from './types';

// 1 — BREATHE (Paciente): aurora sage viva — varredura rotativa lenta + blobs
// que expandem e contraem. Calmo, mas claramente vivo.
function MotionBreathe() {
  return (
    <div className="bg-breathe" aria-hidden="true">
      <span className="breathe-aurora" />
      <span className="breathe-blob b1" />
      <span className="breathe-blob b2" />
      <span className="breathe-blob b3" />
      <span className="breathe-blob b4" />
    </div>
  );
}

// 2 — FLOWS (Empresa): arcos no perímetro conectando nós + pulsos viajantes —
// fluxos de pagamento/repasses entre empresas. Uma máscara apaga o centro para a
// animação viver nas bordas e não cobrir o texto.
function MotionFlows() {
  const cx = 300;
  const cy = 450;
  const nodes = [
    { x: 55, y: 65 },
    { x: 310, y: 42 },
    { x: 568, y: 82 },
    { x: 578, y: 280 },
    { x: 574, y: 510 },
    { x: 555, y: 745 },
    { x: 370, y: 858 },
    { x: 165, y: 862 },
    { x: 28, y: 730 },
    { x: 22, y: 480 },
    { x: 46, y: 230 },
  ];
  const edgePairs: [number, number][] = [
    [0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7], [7, 8], [8, 9], [9, 10], [10, 0],
    [0, 4], [1, 5], [2, 7], [9, 6],
  ];
  const arcs = edgePairs.map(([a, b], i) => {
    const x1 = nodes[a].x;
    const y1 = nodes[a].y;
    const x2 = nodes[b].x;
    const y2 = nodes[b].y;
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;
    const dx = mx - cx;
    const dy = my - cy;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    const push = 52 + i * 6;
    return `M${x1},${y1} Q${(mx + (dx / dist) * push).toFixed(1)},${(my + (dy / dist) * push).toFixed(1)} ${x2},${y2}`;
  });
  return (
    <div className="bg-flows" aria-hidden="true">
      <svg viewBox="0 0 600 900" preserveAspectRatio="xMidYMid slice">
        {arcs.map((d, i) => (
          <path key={`b${i}`} className="flow-base" d={d} />
        ))}
        {arcs.map((d, i) => (
          <path
            key={`f${i}`}
            className="flow-pulse"
            d={d}
            style={{
              animationDelay: `${-(i * 0.85).toFixed(2)}s`,
              animationDuration: `${(4.4 + (i % 5) * 0.65).toFixed(2)}s`,
            }}
          />
        ))}
        {nodes.map((n, i) => (
          <g key={i}>
            <circle className="flow-ring" cx={n.x} cy={n.y} r={10} style={{ animationDelay: `${-(i * 1.1).toFixed(2)}s` }} />
            <circle className="flow-node" cx={n.x} cy={n.y} r={3.5} />
          </g>
        ))}
      </svg>
    </div>
  );
}

// 3 — FLOAT (Cobrança): pequenas partículas douradas subindo, como valor que
// chega em silêncio. Leve, aéreo — "Receber.".
function MotionFloat() {
  const hub = { x: 508, y: 560 };
  const sources = [
    { x: 62, y: 124 },
    { x: 78, y: 300 },
    { x: 54, y: 486 },
    { x: 84, y: 692 },
    { x: 136, y: 838 },
    { x: 560, y: 90 },
  ];
  const lanes = sources.map((s, i) => {
    const mx = (s.x + hub.x) / 2;
    const my = (s.y + hub.y) / 2;
    const dx = mx - 300;
    const dy = my - 450;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    const push = 46 + i * 7;
    return `M${s.x},${s.y} Q${(mx + (dx / dist) * push).toFixed(1)},${(my + (dy / dist) * push).toFixed(1)} ${hub.x},${hub.y}`;
  });
  const pulses = lanes.flatMap((lane, laneIndex) =>
    Array.from({ length: 2 }, (_, pulseIndex) => ({
      path: lane,
      dur: `${(3.8 + laneIndex * 0.42 + pulseIndex * 0.34).toFixed(2)}s`,
      begin: `${-(laneIndex * 0.72 + pulseIndex * 1.15).toFixed(2)}s`,
      r: (2.8 + ((laneIndex + pulseIndex) % 3) * 0.6).toFixed(1),
    })),
  );

  return (
    <div className="bg-float" aria-hidden="true">
      <svg className="float-receive-svg" viewBox="0 0 600 900" preserveAspectRatio="xMidYMid slice">
        <circle className="flow-ring" cx={hub.x} cy={hub.y} r={18} style={{ animationDelay: '-0.9s' }} />
        <circle className="flow-ring" cx={hub.x} cy={hub.y} r={18} style={{ animationDelay: '-2.2s' }} />
        <circle className="flow-node" cx={hub.x} cy={hub.y} r={5} />

        {lanes.map((lane, i) => (
          <path key={`b${i}`} className="flow-base" d={lane} />
        ))}
        {lanes.map((lane, i) => (
          <path
            key={`f${i}`}
            className="flow-pulse"
            d={lane}
            style={{
              animationDelay: `${-(i * 0.85).toFixed(2)}s`,
              animationDuration: `${(4.4 + (i % 5) * 0.65).toFixed(2)}s`,
            }}
          />
        ))}

        {pulses.map((pulse, i) => (
          <circle key={`p${i}`} className="flow-node" r={pulse.r}>
            <animateMotion dur={pulse.dur} begin={pulse.begin} repeatCount="indefinite" path={pulse.path} />
          </circle>
        ))}

        {sources.map((s, i) => (
          <g key={`n${i}`}>
            <circle className="flow-ring" cx={s.x} cy={s.y} r={10} style={{ animationDelay: `${-(i * 1.05).toFixed(2)}s` }} />
            <circle className="flow-node" cx={s.x} cy={s.y} r={3.5} />
          </g>
        ))}
      </svg>
    </div>
  );
}

// 4 — TRELLIS (Admin): grade de diamantes a 45° em que cada célula pulsa e
// ilumina individualmente — a grade é a animação. Mascarada à esquerda para o
// texto permanecer legível.
function MotionTrellis() {
  const step = 52;
  const cols = 18;
  const rows = 20;
  const bleedX = 180;
  const bleedY = -10;
  const rand = (n: number) => {
    const x = Math.sin(n * 17.32) * 43758.5;
    return x - Math.floor(x);
  };
  const cells: { x: number; y: number; dur: string; del: string; peak: string }[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const i = r * cols + c;
      cells.push({
        x: c * step,
        y: r * step,
        dur: (6.5 + rand(i * 3) * 9).toFixed(1),
        del: (-(rand(i * 7) * 18)).toFixed(1),
        peak: (0.13 + rand(i * 11) * 0.27).toFixed(2),
      });
    }
  }
  return (
    <div className="bg-trellis" aria-hidden="true">
      <svg
        className="trellis-svg"
        viewBox={`${-bleedX} ${-bleedY} ${cols * step + bleedX * 2} ${rows * step + bleedY * 2}`}
        preserveAspectRatio="xMinYMid slice"
      >
        <g>
          {cells.map((c, i) => (
            <rect
              key={i}
              className="trellis-cell"
              x={c.x + 1.5}
              y={c.y + 1.5}
              width={step - 3}
              height={step - 3}
              style={{ animationDuration: `${c.dur}s`, animationDelay: `${c.del}s`, '--peak': c.peak } as CSSProperties}
            />
          ))}
        </g>
      </svg>
    </div>
  );
}

export const motions: Record<MotionKind, FC> = {
  breathe: MotionBreathe,
  flows: MotionFlows,
  float: MotionFloat,
  trellis: MotionTrellis,
};
